// cSpell:ignore jaarafrekening Volgt
// Departure settlements (see ISettledDeparture): which leases of a unit can
// still get one, how a lease's end moved since its departure, and which
// costs wait for the readings of the year.

import { toCents } from "./money.utils";
import type { IAlreadySettledLine } from "./types";
import {
  ContractRef,
  DistributionMethod,
  Invoice,
  ISettledDeparture,
  resolveDistributionMethod,
  Settlement,
  SettlementDraftState,
  SettlementScope,
  settledDepartureOf,
} from "./types";

// An open-ended lease carries this end date (see hooks: contractRef).
export const OPEN_ENDED_FROM = "2999-01-01";

// The leases of a unit whose tenant leaves or left: they have an end date,
// and no confirmed departure settlement settled them yet. Every one of them
// stays in the list, the end nearest to today first (decision 7, Lewis
// 2026-10-20): the tenant who just left or leaves soon comes before a
// nine-year lease ending in 2033, or one that ended years ago.
const MS_PER_DAY = 86_400_000;

const dayNumber = (iso: string): number =>
  Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / MS_PER_DAY);

export const departureCandidates = (params: {
  contracts: ContractRef[];
  settlements: Settlement[];
  // Today's calendar day (todayIsoDay).
  today: string;
}): ContractRef[] => {
  const today = dayNumber(params.today);
  const distance = (contract: ContractRef) =>
    Math.abs(dayNumber(contract.endDate) - today);
  const departed = new Set(
    params.settlements
      .map(settledDepartureOf)
      .flatMap(departure => (departure ? [departure.contractId] : [])),
  );
  return params.contracts
    .filter(
      contract =>
        contract.endDate < OPEN_ENDED_FROM && !departed.has(contract.id),
    )
    .sort(
      (a, b) => distance(a) - distance(b) || a.endDate.localeCompare(b.endDate),
    );
};

// The confirmed departure settlement of this lease, if it has one (see
// settledDepartureOf). Drafts, regular settlements and departures of other
// leases are not it; `ownId`, the settlement being made, is left out.
export const existingDepartureOf = (params: {
  settlements: Settlement[];
  contractId: string;
  ownId?: string;
}): Settlement | undefined =>
  params.settlements.find(
    settlement =>
      settlement.id !== params.ownId &&
      settledDepartureOf(settlement)?.contractId === params.contractId,
  );

// Why a departure settlement cannot be made for this lease of the unit: it
// already has one (a lease leaves once), or it is not a lease of the unit
// (an old link, a lease moved or deleted since). Undefined while it can.
// The API refuses both on confirm (rentio-api-next assertDeparture); the
// wizard says so before the broker prepares it.
export type TDepartureBlock =
  | { kind: "exists"; settlement: Settlement }
  | { kind: "notOnUnit" };

export const departureBlockOf = (params: {
  contractId: string;
  // The leases of the unit.
  contracts: Pick<ContractRef, "id">[];
  settlements: Settlement[];
  ownId?: string;
}): TDepartureBlock | undefined => {
  const { contractId, contracts, settlements, ownId } = params;
  const existing = existingDepartureOf({ settlements, contractId, ownId });
  if (existing) return { kind: "exists", settlement: existing };
  if (!contracts.some(contract => String(contract.id) === String(contractId))) {
    return { kind: "notOnUnit" };
  }
  return undefined;
};

// The departures a settlement accounts for (see ISettledDeparture): the
// confirmed departures on its unit, or on the group's units it splits over
// (`settledUnitIds`). Matched on the unit the departure froze, not on a live
// lease: a departure whose lease was deleted since still settled those days,
// and dropping it charged them again, to the owner as vacancy (X41). A
// departure settlement itself settles its lease on its own and takes none.
export const departuresInScope = (params: {
  settlements: Settlement[];
  // The settlement being made, left out.
  ownId?: string;
  scope?: SettlementScope;
  // The settled unit (unit scope).
  unitId?: string;
  // The group's units the settlement splits over (group scope).
  settledUnitIds?: string[];
  isDeparture: boolean;
}): ISettledDeparture[] => {
  const { settlements, ownId, scope, unitId, settledUnitIds, isDeparture } =
    params;
  if (isDeparture) return [];
  const inScope = (departureUnitId: string) =>
    scope === "group"
      ? (settledUnitIds ?? []).includes(String(departureUnitId))
      : unitId != null && String(departureUnitId) === String(unitId);
  return settlements
    .filter(settlement => settlement.id !== ownId)
    .map(settledDepartureOf)
    .filter(
      (departure): departure is ISettledDeparture =>
        !!departure && inScope(departure.unitId),
    );
};

// How a departed lease ends now, next to the last day its departure
// settlement settled (Lewis, 2026-10-09):
// - "later": the tenant stays longer; the settlement of the unit or the group
//   charges the days after it
// - "open": the lease has no end any more, same as later
// - "earlier": the tenant left before it, so the departure charged days they
//   did not live there; only deleting and redoing it corrects that
export type TDepartureLeaseEnd =
  | { kind: "later"; end: string }
  | { kind: "open" }
  | { kind: "earlier"; end: string };

// Undefined while the lease still ends on that day, or is gone.
export const departureLeaseEnd = (params: {
  until: string;
  lease?: Pick<ContractRef, "endDate">;
}): TDepartureLeaseEnd | undefined => {
  if (!params.lease) return undefined;
  const until = params.until.slice(0, 10);
  const end = params.lease.endDate.slice(0, 10);
  if (end >= OPEN_ENDED_FROM) {
    return until >= OPEN_ENDED_FROM ? undefined : { kind: "open" };
  }
  if (end > until) return { kind: "later", end };
  if (end < until) return { kind: "earlier", end };
  return undefined;
};

// A tenant's lease whose end moved since a confirmed settlement (other than
// a departure) settled it (Y11, M07): the day it settled the lease up to
// (the lease end it froze, within the days it settled) and the end now.
// Undefined when the move changes nothing for that settlement: an end that
// moved later than all it settled, or that it never reached.
export const settledLeaseEndChange = (params: {
  // PerContractResult.leaseEnd.
  leaseEnd: string;
  lease?: Pick<ContractRef, "endDate">;
  // The last day the settlement settled anything for the lease (its period,
  // its provision days).
  lastDay: string;
}): { change: TDepartureLeaseEnd; until: string } | undefined => {
  const { lease } = params;
  if (!lease) return undefined;
  const lastDay = params.lastDay.slice(0, 10);
  const frozen = params.leaseEnd.slice(0, 10);
  const until = frozen < lastDay ? frozen : lastDay;
  const change = departureLeaseEnd({ until, lease });
  if (!change) return undefined;
  // A later end matters only when the settlement stopped before its days.
  if (change.kind !== "earlier" && until >= lastDay) return undefined;
  return { change, until };
};

// A group cost split by consumption or by the metering company needs the
// readings of the whole year, which nobody has when a tenant leaves mid-year
// (Lewis, 2026-10-09). A departure settlement does not propose it: the
// settlement of the group charges the departed tenant their part later. With
// an interim statement of the metering company the broker can still take it
// and enter the tenant's amount.
export const awaitsYearlyReadings = (params: {
  invoice: Pick<Invoice, "scope" | "category">;
  methodByCategory?: Record<string, DistributionMethod>;
}): boolean =>
  params.invoice.scope === "common" &&
  resolveDistributionMethod(params.invoice.category, params.methodByCategory, {
    isDeparture: true,
  }) !== "key";

// A cost the departure settlement leaves to the group's yearly settlement,
// as its cost step labels it ("Volgt in de jaarafrekening"): it waits for
// the readings and the broker did not take it along. A cost they ticked is
// settled now, with the tenant's amount or the key.
export const leftForYearlyReadings = (params: {
  invoice: Pick<Invoice, "id" | "scope" | "category">;
  draft: Pick<
    SettlementDraftState,
    "selectedInvoiceIds" | "distributionMethod"
  >;
}): boolean =>
  !params.draft.selectedInvoiceIds[params.invoice.id] &&
  awaitsYearlyReadings({
    invoice: params.invoice,
    methodByCategory: params.draft.distributionMethod,
  });

// A departure split a category by another key than this settlement does
// (decision 2, 2026-10-20): what that moves onto the owner of the departed
// lease's unit, per departure, from the engine's own differences. Only the
// categories both split by a key; nothing when the difference is none.
export const departureKeyDifferences = (params: {
  category: string;
  keyId?: string;
  departures: ISettledDeparture[];
  alreadySettled: IAlreadySettledLine[];
}): { departure: ISettledDeparture; amount: number }[] =>
  params.departures.flatMap(departure => {
    const departureKeyId = departure.keyIdByCategory?.[params.category];
    if (!departureKeyId || !params.keyId || departureKeyId === params.keyId) {
      return [];
    }
    const cents = params.alreadySettled
      .filter(
        line =>
          line.settlementId === departure.settlementId &&
          line.label === params.category,
      )
      .reduce((sum, line) => sum + toCents(line.difference ?? 0), 0);
    return cents === 0 ? [] : [{ departure, amount: cents / 100 }];
  });
